/**
 * Sawyer 08 meds-feeding (A2) — `/med-pass?date=&slot=`: the floor tablet's med
 * pass. The date and the time of day live in the URL so the tablet can stay
 * on "PM" through a refresh. Gated by ROUTE_ACCESS_RULES['/med-pass']
 * (`pets.notes:write`: the six care roles, never the accountant). The list
 * is `MedPassList`; it reads the same `/care/day` as the Meds & feeding
 * report, so the two can never disagree about a dose.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { todayIsoDate } from '../lib/api.js';
import { MedPassList, PASS_SLOTS, type PassSlot } from '../components/meds-feeding/MedPassList.js';
import { formatDayLabel, isIsoDate, shiftIsoDate } from '../components/dashboard/day-list-filters.js';
import './calendar-day.css';

function parseSlot(raw: string | null): PassSlot | null {
  return raw && (PASS_SLOTS as readonly string[]).includes(raw) ? (raw as PassSlot) : null;
}

export function MedPass({ config }: { config: ClientConfig }): JSX.Element {
  const [params, setParams] = useSearchParams();
  const rawDate = params.get('date');
  const date = isIsoDate(rawDate) ? rawDate : todayIsoDate();
  const slot = parseSlot(params.get('slot'));

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) next.set(k, v);
    setParams(next, { replace: true });
  };

  // Stable across renders so MedPassList's "pin the opening slot" effect
  // runs once per change, not on every render.
  const onSlotChange = useCallback(
    (s: PassSlot) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('slot', s);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const dateControl = (
    <div className="ferro-calday__nav">
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
        onClick={() => update({ date: shiftIsoDate(date, -1) })}
        aria-label="Previous day"
        data-testid="med-pass-prev"
      >
        ‹
      </button>
      <div className="ferro-calday__date">
        <p className="ferro-calday__title" data-testid="med-pass-date">
          {formatDayLabel(date)}
          {date === todayIsoDate() ? <span className="ferro-calday__today"> · Today</span> : null}
        </p>
      </div>
      <button
        type="button"
        className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
        onClick={() => update({ date: shiftIsoDate(date, 1) })}
        aria-label="Next day"
        data-testid="med-pass-next"
      >
        ›
      </button>
      {date !== todayIsoDate() ? (
        <button type="button" className="ferro-btn ferro-btn--ghost ferro-mf-btn" onClick={() => update({ date: todayIsoDate() })}>
          Today
        </button>
      ) : null}
    </div>
  );

  return (
    <section className="ferro-admin-page ferro-mf-page" data-testid="med-pass-page">
      <MedPassList
        apiUrl={config.deployment.apiUrl}
        date={date}
        slot={slot}
        onSlotChange={onSlotChange}
        dateControl={dateControl}
      />
    </section>
  );
}
